import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { LucideAngularModule, CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-angular';
import { ToastService, Toast } from '@shared';

/**
 * Renders global toast notifications managed by `ToastService`.
 */
@Component({
  selector: 'app-toast',
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.css',
})
export class ToastComponent {
  /** Shared toast state and dismissal API. */
  readonly toastService = inject(ToastService);
  /** Reactive list of active toast notifications. */
  readonly toasts = this.toastService.toasts;

  /** Icon used for successful operations. */
  readonly CheckCircleIcon   = CheckCircle;
  /** Icon used for failed operations. */
  readonly XCircleIcon       = XCircle;
  /** Icon used for warning notifications. */
  readonly AlertTriangleIcon = AlertTriangle;
  /** Icon used for informational notifications. */
  readonly InfoIcon          = Info;
  /** Icon used by the dismiss button. */
  readonly XIcon             = X;

  /**
   * Selects the icon that matches a toast severity.
   *
   * @param toast Toast notification to render.
   * @returns Lucide icon data for the toast type.
   */
  iconFor(toast: Toast) {
    switch (toast.type) {
      case 'success': return this.CheckCircleIcon;
      case 'error':   return this.XCircleIcon;
      case 'warning': return this.AlertTriangleIcon;
      case 'info':    return this.InfoIcon;
    }
  }

  /**
   * Tracks toast rows by id to keep enter/leave animations stable.
   *
   * @param _ Index supplied by Angular.
   * @param toast Toast notification for the current row.
   * @returns Stable toast id.
   */
  trackById(_: number, toast: Toast) {
    return toast.id;
  }
}
